import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError, AxiosResponse, AxiosResponseHeaders } from "axios";
import { Thunk } from "../../store";
import { BackendBusquedaCasosTareas, getCurrentDomain } from "../../../apis/backend/Api";
import mock_Json from "../../../dataJson/busquedaCasosTareas.json";
import { formatearMensajeBackend, procesarPropiedadMapeo } from "../../../apis/helper/helperApi";
import { TypeStatus } from "../../helpers/constantes";
import { toReadableJsonString } from "../../../utils/parse-json-string";
import { IPayloadBuscaquedaCasosTareas } from "../../../interfaces/backendApi/busquedaCasosTareas";
import { formatearFechaCustom } from "../../../components/common/helper/formatoFecha";

interface State {
  busquedaCasosTareas: any[];
  activoCaseId: string;
  FechaHoraFin: string | null;
  status: TypeStatus;
  processDefinitionIdDisplayName: string;
  processDefinitionIdVersion: string;
  isLoading: boolean;
  error: string | null;
  Success: boolean;
  Mensaje: string;
}

const initialState: State = {
  busquedaCasosTareas: [],
  status: "idle",
  processDefinitionIdDisplayName: "",
  processDefinitionIdVersion: "",
  isLoading: false,
  error: null,
  Success: false,
  Mensaje: "",
  activoCaseId: "",
  FechaHoraFin: "",
};

interface defaultResponse {
  data: any;
  status: number;
  statusText: string;
  headers: AxiosResponseHeaders | {};
  config: any;
  request?: any;
}

const defaultResp: defaultResponse = {
  data: undefined,
  status: 0,
  statusText: "",
  headers: {},
  config: undefined,
};

const busquedaCasosTareasSlice = createSlice({
  name: "busquedaCasosTareas",
  initialState,
  reducers: {
    setBusquedaCasosTareas: (state, action: PayloadAction<any[]>) => {
      state.busquedaCasosTareas = action.payload;
    },
    setActivoCaseId: (state, action: PayloadAction<string>) => {
      state.activoCaseId = action.payload;
    },
    setFechaHoraFin: (state, action: PayloadAction<string>) => {
      state.FechaHoraFin = action.payload;
    },
    setClearBusquedaCasosTareas: (state) => {
      return initialState;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setStatus: (state, action: PayloadAction<TypeStatus>) => {
      state.status = action.payload;
    },
    setSuccess: (state, action: PayloadAction<boolean>) => {
      state.Success = action.payload;
    },
    setMensaje: (state, action: PayloadAction<string>) => {
      state.Mensaje = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    setProcessDefinitionIdDisplayName: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.processDefinitionIdDisplayName = action.payload;
    },
    setProcessDefinitionIdVersion: (state, action: PayloadAction<string>) => {
      state.processDefinitionIdVersion = action.payload;
    },
  },
});

export const setBackendBusquedaCasosTareas =
  (payload: IPayloadBuscaquedaCasosTareas): Thunk =>
    async (dispatch): Promise<AxiosResponse | AxiosError> => {
      dispatch(setIsLoading(true));
      try {
        const response: AxiosResponse = await BackendBusquedaCasosTareas(payload);

        const isError =
          response.data?.Success === false ||
          (response.data?.ResultadoSolicitud !== undefined &&
            response.data?.ResultadoSolicitud !== 200);

        if (isError) {
          dispatch(setIsLoading(false));
          dispatch(setStatus("failed"));
          dispatch(setSuccess(false));
        } else {
          let ordenarLista = [...(response.data?.Data || [])];
          ordenarLista = procesarPropiedadMapeo(ordenarLista, "Prioridad");

          let listaFormateada = ordenarLista.map(
            (e: {
              VencimientoCaso: string;
              FechaHoraInicio: string;
              Vencimiento: string;
              UltimaActualizacion: string;
            }) => ({
              ...e,
              VencimientoCaso: formatearFechaCustom(e.VencimientoCaso),
              FechaHoraInicio: formatearFechaCustom(e.FechaHoraInicio),
              Vencimiento: formatearFechaCustom(e.Vencimiento),
              UltimaActualizacion: formatearFechaCustom(e.UltimaActualizacion),
            }),
          );
          dispatch(setBusquedaCasosTareas(listaFormateada));
          dispatch(setSuccess(true));
          dispatch(setIsLoading(false));
          dispatch(setStatus("succeeded"));
        }
        dispatch(setMensaje(formatearMensajeBackend(response.data.Mensaje)));

        return response.data;
      } catch (error: any) {
        dispatch(setIsLoading(false));
        dispatch(setSuccess(false));
        dispatch(setStatus("failed"));

        if (error.response?.status === 401) {
          dispatch(setStatus("noAutorice"));
          dispatch(
            setMensaje(
              formatearMensajeBackend(
                "Tu sesión ha expirado. Vuelve a iniciar sesión",
              ),
            ),
          );
        } else if (error.response) {
          console.error(
            "Error de servidor:",
            error.response.status,
            error.response.data,
          );

          const errorData = error.response.data;
          const mensajeBackend = errorData?.Mensaje;
          const resultadoSolicitud = errorData?.ResultadoSolicitud;

          // Si el backend devolvió un objeto de respuesta con ResultadoSolicitud !== 200 o Success === false
          if (
            mensajeBackend &&
            (resultadoSolicitud !== 200 || errorData?.Success === false)
          ) {
            dispatch(setMensaje(formatearMensajeBackend(mensajeBackend)));
          } else {
            dispatch(
              setMensaje(
                `Error ${error.response.status}: Ocurrió un problema en el servidor.`,
              ),
            );
          }
        } else if (error.request) {
          console.error("Error de conexión:", error.message);
          dispatch(
            setMensaje(
              "No se pudo conectar al servidor. Por favor, revisa tu conexión a internet o tu VPN.",
            ),
          );
        } else {
          console.error("Error de red o solicitud:", error.message);
          dispatch(
            setMensaje(
              "Ocurrió un problema con la red. Por favor, verifica tu conexión.",
            ),
          );
        }
        return error as AxiosError;
      } finally {
        dispatch(setIsLoading(false));
        if (getCurrentDomain() === "localhost") {
          dispatch(setIsLoading(true));
          let response: AxiosResponse = defaultResp;
          const mockData = JSON.parse(JSON.stringify(mock_Json));
          response.data = mockData;

          const isErrorMock =
            mockData?.Success === false ||
            (mockData?.ResultadoSolicitud !== undefined &&
              mockData?.ResultadoSolicitud !== 200);

          if (isErrorMock) {
            dispatch(setIsLoading(false));
            dispatch(setStatus("failed"));
            dispatch(setSuccess(false));
          } else {
            let ordenarLista = [...(response.data?.Data || [])];
            ordenarLista = procesarPropiedadMapeo(ordenarLista, "Prioridad");

            let listaFormateada = ordenarLista.map(
              (e: {
                VencimientoCaso: string;
                FechaHoraInicio: string;
                Vencimiento: string;
                UltimaActualizacion: string;
              }) => ({
                ...e,
                VencimientoCaso: formatearFechaCustom(e.VencimientoCaso),
                FechaHoraInicio: formatearFechaCustom(e.FechaHoraInicio),
                Vencimiento: formatearFechaCustom(e.Vencimiento),
                UltimaActualizacion: formatearFechaCustom(e.UltimaActualizacion),
              }),
            );
            dispatch(setBusquedaCasosTareas(listaFormateada));
            dispatch(setIsLoading(false));
            dispatch(setStatus("succeeded"));
            dispatch(setSuccess(true));
          }
          dispatch(setMensaje(formatearMensajeBackend(response.data.Mensaje)));
        }
      }
    };

export const setBackendClearBusquedaCasosTareas =
  (): Thunk =>
    async (dispatch): Promise<AxiosResponse | AxiosError> => {
      dispatch(setIsLoading(true));
      try {
        let response: AxiosResponse = defaultResp;
        dispatch(setBusquedaCasosTareas([]));
        dispatch(setIsLoading(false));
        dispatch(setStatus("idle"));
        dispatch(setSuccess(false));
        dispatch(setMensaje(""));
        dispatch(setProcessDefinitionIdDisplayName(""));
        dispatch(setProcessDefinitionIdVersion(""));
        dispatch(setActivoCaseId(""));
        dispatch(setFechaHoraFin(""));
        return response.data;
      } catch (error: any) {
        dispatch(setIsLoading(false));
        dispatch(setStatus("failed"));
        return error as AxiosError;
      } finally {
        dispatch(setIsLoading(false));
      }
    };

export const {
  setBusquedaCasosTareas,
  setActivoCaseId,
  setFechaHoraFin,
  setClearBusquedaCasosTareas,
  setIsLoading,
  setStatus,
  setSuccess,
  setMensaje,
  setError,
  setProcessDefinitionIdDisplayName,
  setProcessDefinitionIdVersion,
} = busquedaCasosTareasSlice.actions;

export default busquedaCasosTareasSlice.reducer;
